import React, { useEffect, useState } from "react";
import { Add, Close, DeleteOutline, SaveOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import WorkflowCanvasIcon from "./WorkflowCanvasIcon";
import WorkflowFormFieldRow from "./WorkflowFormFieldRow";
import {
  createWorkflowFormField,
  WORKFLOW_CANVAS_LABELS,
  WorkflowCanvas,
  WorkflowCanvasType,
  WorkflowFormField,
} from "./workflowBuilderData";
import WorkflowApprovalPanel from "./WorkflowApprovalPanel";
import WorkflowCancelPanel from "./WorkflowCancelPanel";
import WorkflowConditionalPanel from "./WorkflowConditionalPanel";
import WorkflowEmailPanel from "./WorkflowEmailPanel";
import WorkflowPausePanel from "./WorkflowPausePanel";
import WorkflowTaskPanel from "./WorkflowTaskPanel";
import WorkflowWebServicePanel from "./WorkflowWebServicePanel";
import { workflowVariablesBefore } from "./workflowGraphMapper";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";
import WorkflowPersonSourceField, { WorkflowPersonValue } from "./WorkflowPersonSourceField";

interface WorkflowCanvasDialogProps {
  canvas: WorkflowCanvas | null;
  canvases: WorkflowCanvas[];
  open: boolean;
  onLiveChange: (canvas: WorkflowCanvas) => void;
  onSave: () => void;
  onDiscard: () => void;
  onDelete: (canvasId: string) => void;
  /** Validation issues for this canvas and its fields (by field id), shown inline. */
  canvasIssues?: string[];
  fieldIssues?: Record<string, string[]>;
}

const TYPE_TAB_LABELS: Partial<Record<WorkflowCanvasType, string>> = {
  form: "Form fields",
  approval: "Approval",
  task: "Task list",
  conditional: "Conditions",
  webservice: "Web service call",
  email: "Email",
  pause: "Timing",
  cancel: "Cancel note",
};

const ownerHintFor = (type: WorkflowCanvasType) =>
  type === "form" || type === "finalize"
    ? "Optional here — defaults to the person running the case."
    : "The person responsible if this canvas needs attention (e.g. a web-service failure).";

const tabLabel = (label: string, dirty: boolean) => (
  <>
    {label}
    {dirty ? (
      <span className="workflow-canvas-dialog__dirty-dot" aria-hidden="true">
        •
      </span>
    ) : null}
  </>
);

/**
 * The not-yet-available "Soon" tabs (Form preview, Rules - Epic 80429) are hidden for now. They are
 * kept, not removed: set this to true to show them again once those tabs are built.
 */
export const SHOW_PLANNED_CANVAS_TABS = false;

/** A tab that is visible but not selectable yet — "planned for a later phase" (Epic 80429). */
const soonTabLabel = (label: string) => (
  <>
    {label}
    <span className="workflow-canvas-dialog__soon-badge">Soon</span>
  </>
);

const WorkflowCanvasDialog: React.FC<WorkflowCanvasDialogProps> = ({
  canvas,
  canvases,
  open,
  onLiveChange,
  onSave,
  onDiscard,
  onDelete,
  canvasIssues = [],
  fieldIssues = {},
}) => {
  const readOnly = useWorkflowReadOnly();
  const [activeTab, setActiveTab] = useState(0);
  const [deleteArmed, setDeleteArmed] = useState(false);
  // Prototype `dirtyTabs`: a tab stays marked until Save & close / Discard, even if the value is
  // edited back. Settings is always index 0; the type tab (Conditions, Form fields, …) is 1.
  const [dirtyTabs, setDirtyTabs] = useState<ReadonlySet<number>>(() => new Set());
  // Prototype `confirmInPlace`: the control you clicked (Discard or X) is armed for 2.6s, same
  // window as Delete canvas on the flow. A second click on that control confirms; anything else
  // disarms. There is no second dialog.
  const [exitArmed, setExitArmed] = useState<"discard" | "close" | null>(null);
  // 768px is SCSS's `$md`, not MUI's `md` (900px): this has to switch on exactly the same width as
  // the `@media (max-width: $md)` block in WorkflowBuilder.scss that styles the fullscreen paper.
  const isSmallScreen = useMediaQuery("(max-width:768px)");

  useEffect(() => {
    setActiveTab(0);
    setDeleteArmed(false);
    setDirtyTabs(new Set());
    setExitArmed(null);
  }, [canvas?.id]);

  useEffect(() => {
    if (!exitArmed) return undefined;
    const tid = window.setTimeout(() => setExitArmed(null), 2600);
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-workflow-exit-arm]")) return;
      setExitArmed(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.clearTimeout(tid);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [exitArmed]);

  // Delete canvas confirms in place like the tile's delete: "Confirm?" for 2.6s, and any click
  // anywhere but the button itself disarms it.
  useEffect(() => {
    if (!deleteArmed) return undefined;
    const tid = window.setTimeout(() => setDeleteArmed(false), 2600);
    const onMouseDown = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest("[data-workflow-delete-arm]")) return;
      setDeleteArmed(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      window.clearTimeout(tid);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [deleteArmed]);

  if (!canvas) return null;

  const typeTab = TYPE_TAB_LABELS[canvas.type];

  const handleTabChange = (_event: React.SyntheticEvent, value: number) => {
    setActiveTab(value);
  };

  const applyLiveChange = (next: WorkflowCanvas) => {
    // Every input is disabled in read-only mode; this keeps a stray keyboard path from editing.
    if (readOnly) return;
    setDirtyTabs((current) => {
      if (current.has(activeTab)) return current;
      const nextTabs = new Set(current);
      nextTabs.add(activeTab);
      return nextTabs;
    });
    onLiveChange(next);
  };

  const requestClose = (source: "discard" | "close") => {
    if (readOnly || dirtyTabs.size === 0 || exitArmed === source) {
      onDiscard();
      return;
    }
    setExitArmed(source);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    applyLiveChange({ ...canvas, [event.target.name]: event.target.value });
  };

  const handleOwnerChange = ({ source, reference }: WorkflowPersonValue) => {
    applyLiveChange({ ...canvas, ownerSource: source, owner: reference });
  };

  const updateFields = (fields: WorkflowFormField[]) => applyLiveChange({ ...canvas, fields });

  const handleAddField = () => updateFields([...canvas.fields, createWorkflowFormField()]);

  const handleFieldChange = (id: string, patch: Partial<WorkflowFormField>) => {
    updateFields(canvas.fields.map((field) => (field.id === id ? { ...field, ...patch } : field)));
  };

  const handleRemoveField = (id: string) => {
    updateFields(canvas.fields.filter((field) => field.id !== id));
  };

  const handleReorderField = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    const from = canvas.fields.findIndex((field) => field.id === sourceId);
    const to = canvas.fields.findIndex((field) => field.id === targetId);
    if (from < 0 || to < 0) return;
    const reordered = [...canvas.fields];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    updateFields(reordered);
  };

  const handleMoveField = (id: string, direction: "up" | "down") => {
    const from = canvas.fields.findIndex((field) => field.id === id);
    const to = direction === "up" ? from - 1 : from + 1;
    if (from < 0 || to < 0 || to >= canvas.fields.length) return;
    const reordered = [...canvas.fields];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    updateFields(reordered);
  };

  const handleApprovalChange = (approval: WorkflowCanvas["approval"]) => {
    applyLiveChange({ ...canvas, approval });
  };

  const handleTasksChange = (tasks: WorkflowCanvas["tasks"]) => {
    applyLiveChange({ ...canvas, tasks });
  };

  const handlePathsChange = (paths: WorkflowCanvas["paths"]) => {
    applyLiveChange({ ...canvas, paths });
  };

  const handleWebServiceChange = (webService: WorkflowCanvas["webService"]) => {
    applyLiveChange({ ...canvas, webService });
  };

  const handleEmailChange = (email: WorkflowCanvas["email"]) => {
    applyLiveChange({ ...canvas, email });
  };

  const handlePauseChange = (pause: WorkflowCanvas["pause"]) => {
    applyLiveChange({ ...canvas, pause });
  };

  const handleCancelChange = (cancel: WorkflowCanvas["cancel"]) => {
    applyLiveChange({ ...canvas, cancel });
  };

  const handleDelete = () => {
    if (deleteArmed) {
      onDelete(canvas.id);
    } else {
      setDeleteArmed(true);
    }
  };

  const isDirty = dirtyTabs.size > 0;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Prototype close is X / Discard / Escape — not the dimmed overlay.
        if (reason === "backdropClick") return;
        requestClose("close");
      }}
      maxWidth={false}
      // MUI's own fullScreen, not CSS: the paper keeps `margin: 32px` and
      // `max-height: calc(100% - 64px)` no matter what width/height a stylesheet asks for, so a
      // hand-rolled 100vw/100vh paper sits inset and overflowing. `fullScreen` clears all three.
      fullScreen={isSmallScreen}
      classes={{ paper: "workflow-canvas-dialog" }}
      PaperProps={{ "aria-label": `${canvas.name} canvas editor` }}
    >
      <DialogTitle className="workflow-canvas-dialog__header">
        <span
          className={`workflow-builder__canvas-icon workflow-builder__canvas-icon--${canvas.type}`}
        >
          <WorkflowCanvasIcon type={canvas.type} />
        </span>
        <span className="workflow-canvas-dialog__header-copy">
          {/* Truncated to one line by the stylesheet, so carry the full name in `title` for the
              cases where a long one is cut off. The Settings tab's Canvas name field has it too. */}
          <Typography component="h2" title={canvas.name}>
            {canvas.name}
          </Typography>
          <Typography>{WORKFLOW_CANVAS_LABELS[canvas.type]} canvas</Typography>
        </span>
        {exitArmed === "close" ? (
          <Button
            data-workflow-exit-arm=""
            className="workflow-canvas-dialog__close-confirm"
            variant="outlined"
            size="small"
            onClick={() => requestClose("close")}
          >
            Discard?
          </Button>
        ) : (
          <IconButton
            data-workflow-exit-arm=""
            onClick={() => requestClose("close")}
            aria-label="Close canvas editor"
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      <Tabs
        className="workflow-canvas-dialog__tabs"
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Canvas configuration tabs"
      >
        <Tab label={tabLabel("Settings", dirtyTabs.has(0))} />
        {typeTab && <Tab label={tabLabel(typeTab, dirtyTabs.has(1))} />}
        {/* After the real tabs, so their indexes (and the dirty markers keyed on them) don't move.
            Finalize is Settings-only, so it has no Rules tab. */}
        {SHOW_PLANNED_CANVAS_TABS && canvas.type === "form" && (
          <Tab disabled label={soonTabLabel("Form preview")} />
        )}
        {SHOW_PLANNED_CANVAS_TABS && canvas.type !== "finalize" && (
          <Tab disabled label={soonTabLabel("Rules")} />
        )}
      </Tabs>

      <DialogContent className="workflow-canvas-dialog__content">
        {canvasIssues.length > 0 && (
          <Alert severity="error" className="workflow-canvas-dialog__issues">
            <ul aria-label="Validation issues on this canvas">
              {canvasIssues.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}
        {activeTab === 0 ? (
          <Box className="workflow-canvas-dialog__settings">
            <Box>
              <Typography component="h3">Canvas settings</Typography>
              <Typography>
                These details appear on the flow tile and help the assigned owner understand this
                step.
              </Typography>
            </Box>
            <TextField
              name="name"
              label="Canvas name"
              value={canvas.name}
              onChange={handleTextChange}
              required
              fullWidth
              size="small"
              disabled={readOnly}
            />
            <TextField
              name="summary"
              label="Canvas summary"
              value={canvas.summary}
              onChange={handleTextChange}
              multiline
              rows={3}
              fullWidth
              size="small"
              disabled={readOnly}
            />
            <WorkflowPersonSourceField
              idPrefix={`${canvas.id}-owner`}
              sourceLabel="Canvas owner"
              noneLabel="Not set"
              value={{ source: canvas.ownerSource, reference: canvas.owner }}
              onChange={handleOwnerChange}
              helperText={ownerHintFor(canvas.type)}
            />
          </Box>
        ) : canvas.type === "form" ? (
          <Box className="workflow-canvas-dialog__type-panel">
            <Box className="workflow-canvas-dialog__type-heading">
              <span>
                <Typography component="h3">
                  {typeTab} ({canvas.fields.length})
                </Typography>
                <Typography>
                  Each entry is configured here and rendered when the case reaches this canvas.
                </Typography>
              </span>
            </Box>

            {canvas.fields.length === 0 ? (
              <Box className="workflow-canvas-dialog__empty">
                <Typography>No fields yet — add the first one to get started.</Typography>
              </Box>
            ) : (
              canvas.fields.map((field, index) => (
                <WorkflowFormFieldRow
                  key={field.id}
                  field={field}
                  index={index}
                  count={canvas.fields.length}
                  onChange={handleFieldChange}
                  onMove={handleMoveField}
                  onRemove={handleRemoveField}
                  onReorder={handleReorderField}
                  issues={fieldIssues[field.id]}
                />
              ))
            )}

            {!readOnly && (
              <Button
                className="workflow-canvas-dialog__mini-add"
                startIcon={<Add />}
                onClick={handleAddField}
              >
                Add field
              </Button>
            )}
          </Box>
        ) : canvas.type === "approval" ? (
          <WorkflowApprovalPanel
            approval={canvas.approval}
            canvases={canvases}
            currentCanvasId={canvas.id}
            onChange={handleApprovalChange}
          />
        ) : canvas.type === "task" ? (
          <WorkflowTaskPanel tasks={canvas.tasks} onChange={handleTasksChange} />
        ) : canvas.type === "conditional" ? (
          <WorkflowConditionalPanel
            paths={canvas.paths}
            canvases={canvases}
            currentCanvasId={canvas.id}
            onChange={handlePathsChange}
          />
        ) : canvas.type === "webservice" ? (
          <WorkflowWebServicePanel
            config={canvas.webService}
            variables={workflowVariablesBefore(canvases, canvas.id)}
            onChange={handleWebServiceChange}
          />
        ) : canvas.type === "email" ? (
          <WorkflowEmailPanel config={canvas.email} onChange={handleEmailChange} />
        ) : canvas.type === "pause" ? (
          <WorkflowPausePanel
            config={canvas.pause}
            canvases={canvases}
            currentCanvasId={canvas.id}
            onChange={handlePauseChange}
          />
        ) : canvas.type === "cancel" ? (
          <WorkflowCancelPanel config={canvas.cancel} onChange={handleCancelChange} />
        ) : (
          <Box className="workflow-canvas-dialog__soon">
            <Typography component="h4">{typeTab} configuration is planned for a later phase</Typography>
            <Typography>
              The tab remains visible so the future workflow shape is clear.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="workflow-canvas-dialog__actions">
        {readOnly ? (
          // View mode: nothing to delete, discard or save.
          <Button variant="contained" size="small" onClick={onDiscard}>
            Close
          </Button>
        ) : (
          <>
            <Button
              className={`workflow-canvas-dialog__delete${
                deleteArmed ? " workflow-canvas-dialog__delete--armed" : ""
              }`}
              variant="outlined"
              size="small"
              startIcon={<DeleteOutline />}
              onClick={handleDelete}
              data-workflow-delete-arm=""
              aria-label={deleteArmed ? `Confirm delete ${canvas.name}` : undefined}
            >
              {deleteArmed ? "Confirm?" : "Delete canvas"}
            </Button>
            <span
              className={`workflow-canvas-dialog__dirty${
                isDirty ? " workflow-canvas-dialog__dirty--on" : ""
              }`}
              role="status"
            >
              {isDirty ? "Unsaved changes" : null}
            </span>
            <Button
              className={`workflow-canvas-dialog__discard${
                exitArmed === "discard" ? " workflow-canvas-dialog__discard--armed" : ""
              }`}
              data-workflow-exit-arm=""
              variant="outlined"
              size="small"
              onClick={() => requestClose("discard")}
            >
              {exitArmed === "discard" ? "Discard changes?" : "Discard"}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveOutlined />}
              onClick={onSave}
            >
              Save &amp; close
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowCanvasDialog;
